// src/components/pages/AttorneysPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SeoHead from "../section/SeoHead";
import AttorneyHeroSection from "../section/AttorneyHeroSection";
import FiltersSection from "../section/FiltersSection";
import AttorneyList from "../section/AttorneyList";
import TicketModal from "../tickets/TicketModal";
import { useLawyers } from "../../hooks/useLawyers";
import Swal from "sweetalert2";

const ITEMS_PER_PAGE = 12;

const AttorneysPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    searchTerm: "",
    stateFilter: "",
    cityFilter: "",
    hasOffice: false,
    minRating: 0,
    minReviews: 0,
    sortOption: "top",
  });

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAttorneyId, setSelectedAttorneyId] = useState(null);

  const { data, loading, error } = useLawyers(page, ITEMS_PER_PAGE);

  const openModal = (id) => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: t("attorneys_page.alerts.login_required.title"),
        text: t("attorneys_page.alerts.login_required.text"),
        confirmButtonText: t("attorneys_page.alerts.login_required.confirm"),
        confirmButtonColor: "#3B82F6",
      }).then(() => {
        // توجيه إلى صفحة تسجيل الدخول
        navigate(`/${i18n.language}/login`);
      });
      return;
    }

    // إذا كان مسجل دخول
    setSelectedAttorneyId(id);
    setModalOpen(true);
  };

  const filteredAndSorted = useMemo(() => {
    if (!data?.data) return { attorneys: [], count: 0 };

    let list = [...data.data];

    const q = filters.searchTerm.toLowerCase();
    if (q) {
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        (a.office?.name.toLowerCase().includes(q))
      );
    }

    if (filters.stateFilter) list = list.filter(a => a.office?.state === filters.stateFilter);
    if (filters.cityFilter) list = list.filter(a => a.office?.city.toLowerCase().includes(filters.cityFilter.toLowerCase()));
    if (filters.hasOffice) list = list.filter(a => !!a.office);
    list = list.filter(a => parseFloat(a.average_rating) >= filters.minRating);
    list = list.filter(a => a.total_ratings >= filters.minReviews);

    switch (filters.sortOption) {
      case "top":
        list.sort((a, b) => (parseFloat(b.average_rating) - parseFloat(a.average_rating)) || (b.total_ratings - a.total_ratings));
        break;
      case "reviews":
        list.sort((a, b) => b.total_ratings - a.total_ratings);
        break;
      case "name":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "ratingAsc":
        list.sort((a, b) => parseFloat(a.average_rating) - parseFloat(b.average_rating));
        break;
      default:
        return;
    }

    return { attorneys: list, count: list.length };
  }, [data?.data, filters]);

  useEffect(() => setPage(1), [filters]);

  const handleSubmit = async (payload) => {
    await new Promise(r => setTimeout(r, 1500));
  };

  const meta = data?.meta || { current_page: 1, last_page: 1, total: 0 };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SeoHead
        title={t("attorneys_page.seo.title")}
        description={t("attorneys_page.seo.description")}
        keywords={t("attorneys_page.seo.keywords")}
        canonicalUrl="https://citizenhubimmigration.com/attorneys"
      />

      <AttorneyHeroSection />

      <main className="relative mt-0 pt-14 pb-28 lg:pt-16 lg:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col space-y-10 lg:space-y-14">
            <FiltersSection
              filters={filters}
              setFilters={setFilters}
              filteredCount={filteredAndSorted.count}
              totalCount={meta.total}
              onReset={() => setFilters({
                searchTerm: "", stateFilter: "", cityFilter: "", hasOffice: false,
                minRating: 0, minReviews: 0, sortOption: "top"
              })}
            />

            <div style={{ marginTop: "80px", marginBottom: "80px" }}>
              {error ? (
                <div className="text-center py-10 text-red-600">{t("attorneys_page.alerts.error_loading") || error}</div>
              ) : (
                <AttorneyList
                  attorneys={filteredAndSorted.attorneys}
                  loading={loading}
                  onOpenTicket={openModal} // تمرير الدالة
                  currentPage={page}
                  totalPages={meta.last_page}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Ticket Modal */}
      <TicketModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        prefill={{ attorneyId: selectedAttorneyId }}
      />
    </div>
  );
};

export default AttorneysPage;